import React, { useRef } from 'react'
import { Box, Typography, Link, IconButton } from '@mui/material'
import { ChevronLeft, ChevronRight, Star } from '@mui/icons-material'
import { useTranslations, useLocale } from 'next-intl'

import LazyImage from '@/src/components/common/ui/LazyImage'

interface Hotel {
    id: string
    title: string
    location: string
    rating: number
    imageUrl: string
    href: string
}

interface TrendingHotelsProps {
    title: string
    hotels: Hotel[]
}

export const TrendingHotels: React.FC<TrendingHotelsProps> = ({ title, hotels }) => {
    const t = useTranslations('homePage.headings')
    const locale = useLocale()
    const isRTL = locale === 'ar'
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    if (!hotels || hotels.length === 0) {
        return null
    }

    const scroll = (direction: 'left' | 'right') => {
        if (!scrollContainerRef.current) return

        const container = scrollContainerRef.current
        const scrollAmount = 280 // card width (250px) + gap (24px) + some padding
        const scrollPosition = direction === 'left' ? -scrollAmount : scrollAmount

        container.scrollBy({
            left: isRTL ? -scrollPosition : scrollPosition,
            behavior: 'smooth'
        })
    }

    return (
        <Box
            sx={{
                display: { xs: 'block', md: 'none' },
                px: 2,
                py: 0,
                bgcolor: 'grey.50'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    mb: 1
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '1.125rem'
                    }}
                >
                    {title}
                </Typography>
            </Box>

            {/* Carousel Container */}
            <Box
                sx={{
                    position: 'relative',
                    direction: isRTL ? 'rtl' : 'ltr'
                }}
            >
                {/* Navigation Arrows */}
                <IconButton
                    onClick={() => scroll('left')}
                    sx={{
                        display: { xs: 'flex', sm: 'none' },
                        position: 'absolute',
                        left: isRTL ? 'auto' : 0,
                        right: isRTL ? 0 : 'auto',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        width: 36,
                        height: 36,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 1)'
                        }
                    }}
                >
                    {isRTL ? <ChevronRight /> : <ChevronLeft />}
                </IconButton>

                <IconButton
                    onClick={() => scroll('right')}
                    sx={{
                        display: { xs: 'flex', sm: 'none' },
                        position: 'absolute',
                        right: isRTL ? 'auto' : 0,
                        left: isRTL ? 0 : 'auto',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        width: 36,
                        height: 36,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 1)'
                        }
                    }}
                >
                    {isRTL ? <ChevronLeft /> : <ChevronRight />}
                </IconButton>

                {/* Carousel */}
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        scrollBehavior: 'smooth',
                        gap: 2,
                        direction: isRTL ? 'rtl' : 'ltr',
                        pb: 1,
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    }}
                >
                    {hotels.map(hotel => (
                        <Link
                            key={hotel.id}
                            href={hotel.href}
                            target="_blank"
                            sx={{
                                position: 'relative',
                                borderRadius: 2,
                                overflow: 'hidden',
                                height: '180px',
                                width: '280px',
                                minWidth: '250px',
                                bgcolor: 'common.white',
                                display: 'block',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                scrollSnapAlign: 'start',
                                flexShrink: 0
                            }}
                        >
                            {/* Trending Chip - Top Right */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                                    borderRadius: '20px',
                                    px: 1.5,
                                    py: 0.5,
                                    zIndex: 2
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        color: 'common.white'
                                    }}
                                >
                                    {t('trending')}
                                </Typography>
                            </Box>

                            {/* Hotel Image */}
                            <LazyImage src={hotel.imageUrl} alt={hotel.title} sizes="(max-width: 768px) 250px, 250px" />

                            {/* Text Overlay with Grey Background */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    p: 1.5,
                                    zIndex: 1,
                                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                                    background:
                                        'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%)'
                                }}
                            >
                                {/* Hotel Title */}
                                <Typography
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: 'common.white',
                                        mb: 0.5,
                                        lineHeight: 1.3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {hotel.title}
                                </Typography>

                                {/* Rating and Location */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mt: 0.5
                                    }}
                                >
                                    {/* Star Rating */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.25
                                        }}
                                    >
                                        {Array.from({ length: hotel.rating }).map((_, index) => (
                                            <Star
                                                key={index}
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    color: '#FFD700',
                                                    fill: '#FFD700'
                                                }}
                                            />
                                        ))}
                                    </Box>

                                    {/* Separator */}
                                    <Box
                                        sx={{
                                            width: '1px',
                                            height: '12px',
                                            bgcolor: 'rgba(255, 255, 255, 0.5)'
                                        }}
                                    />

                                    {/* Location */}
                                    <Typography
                                        sx={{
                                            fontSize: '0.875rem',
                                            fontWeight: 400,
                                            color: 'common.white'
                                        }}
                                    >
                                        {hotel.location}
                                    </Typography>
                                </Box>
                            </Box>
                        </Link>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

export type { TrendingHotelsProps }
